import { useEffect, useRef } from "react";
import type { Lang, Question } from "../types.ts";
import type { LoadedCatalog } from "../catalog.ts";
import type { QuestionProgress } from "../state/store.ts";
import { pick } from "../catalog.ts";
import { t } from "../i18n/ui.ts";

interface Props {
  open: boolean;
  lang: Lang;
  question: Question;
  catalog: LoadedCatalog;
  progress?: QuestionProgress;
  onClose: () => void;
}

/**
 * The ⓘ sheet from the official app: where the question sits in the catalogue,
 * and how you have done on it so far.
 */
export function QuestionDetail({ open, lang, question, catalog, progress, onClose }: Props) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  /** Full category path, root first, e.g. "Basic knowledge › Danger theory". */
  const byId = new Map(catalog.categories.map((c) => [c.id, c]));
  const trail: string[] = [];
  let node = question.categoryIds.length
    ? byId.get(question.categoryIds[question.categoryIds.length - 1]!)
    : undefined;
  const guard = new Set<number>();
  while (node && !guard.has(node.id)) {
    guard.add(node.id);
    trail.unshift(pick(node.titles, lang));
    node = node.parent == null ? undefined : byId.get(node.parent);
  }

  const answered = (progress?.correct ?? 0) + (progress?.wrong ?? 0);
  const lastSeen = progress?.updatedAt
    ? new Date(progress.updatedAt).toLocaleDateString(
        lang === "zh" ? "zh-Hans" : lang === "en" ? "en-GB" : "de-DE"
      )
    : null;

  async function share() {
    const url = `${location.origin}/practice/all/${encodeURIComponent(question.number)}`;
    const text = pick(question.text, lang);
    try {
      if (navigator.share) {
        await navigator.share({ title: question.number, text, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      // Cancelled, or no permission — nothing to recover from.
    }
  }

  return (
    <dialog
      ref={ref}
      className="dialog qdetail"
      onClose={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      aria-labelledby="qdetail-title"
    >
      <h2 id="qdetail-title">{t(lang, "questionDetail")}</h2>

      <dl className="qdetail__list">
        <div className="qdetail__row">
          <dt>{t(lang, "detailNumber")}</dt>
          <dd translate="no">{question.number}</dd>
        </div>

        {trail.length > 0 && (
          <div className="qdetail__row">
            <dt>{t(lang, "detailTopic")}</dt>
            <dd>{trail.join(" › ")}</dd>
          </div>
        )}

        <div className="qdetail__row">
          <dt>{t(lang, "detailPoints")}</dt>
          <dd>{question.points}</dd>
        </div>

        <div className="qdetail__row">
          <dt>{t(lang, "detailPractised")}</dt>
          <dd>
            {answered === 0 ? (
              t(lang, "detailNeverPractised")
            ) : (
              <>
                {lastSeen && <div>{t(lang, "detailLastPractised", { date: lastSeen })}</div>}
                <div>
                  <strong className="qdetail__wrong">{progress?.wrong ?? 0}</strong>{" "}
                  {t(lang, "detailTimesWrong")},{" "}
                  <strong className="qdetail__right">{progress?.correct ?? 0}</strong>{" "}
                  {t(lang, "detailTimesRight")}
                </div>
              </>
            )}
          </dd>
        </div>
      </dl>

      <div className="dialog__actions">
        <button type="button" className="btn" onClick={share}>
          {t(lang, "share")}
        </button>
        <button type="button" className="btn btn--primary" onClick={onClose} autoFocus>
          {t(lang, "ok")}
        </button>
      </div>
    </dialog>
  );
}
